import React, { useEffect, useMemo, useState } from 'react';
import { MissionPromotionSection, MissionPromotionSummary } from '../components/common/mission/MissionPromotion';
import { Image, Modal, Platform, Pressable, ScrollView, SectionList, View, useWindowDimensions } from 'react-native';
import { SvgUri } from 'react-native-svg';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  faAward,
  faBasketShopping,
  faBullseye,
  faChild,
  faCheckDouble,
  faChevronDown,
  faChevronUp,
  faCircleXmark,
  faEye,
  faLocation,
  faLocationCheck,
  faMapLocationDot,
  faPenNib,
  faReceipt,
  faStore,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import AuthGuard from '../components/common/AuthGuard';
import CustomButton from '../components/common/CustomButton';
import CustomMarkdown from '../components/common/CustomMarkDown';
import Label, { LabelType } from '../components/common/Label';
import Loading from '../components/common/Loading';
import LoggedInRestrictedArea from '../components/common/LoggedInRestrictedArea';
import MissionReferPopup from '../components/common/mission/MissionReferPopup';
import { REFERRAL_PURPLE } from '../components/common/mission/ReferralBadge';
import NavigationButton from '../components/common/NavigationButton';
import Overlay from '../components/common/Overlay';
import ProfileModal from '../components/myaccount/myprofile/ProfileModal';
import {
  GetMissionReferralDetailsResponse,
  GetMissionReferralListDto,
  GetMissionReferralListGroupDto,
  GetMissionReferralMissionDetailsDto,
  MissionAssignationStatus,
  MissionConfigurationDetailType,
  MissionReferralListGroup,
  MissionReferralStatus,
  MissionsClient,
  RelatedMinorRelationship,
} from '../api/api.g';
import useApi from '../hooks/api-hook';
import useStyles from '../hooks/styles-hook';
import { useAppDispatch, useAppSelector } from '../hooks/store-hook';
import useCustomTheme from '../hooks/theme-hook';
import useTranslation from '../hooks/translation-hook';
import { LoggedState } from '../models/AuthModelsCommon';
import { StylesHelper } from '../models/helpers/stylesHelper';
import { lightBoxActions } from '../store/lightBoxSlice';
import { missionsActions } from '../store/missionsSlice';
import pin from '../../assets/pin-selected.png';
import { readMissions } from '../helpers/missionRead';

enum NavigationState {
  List,
  Details,
}

type ReferralDetailAccordionSection = 'admissibility' | 'conditions';

interface CachedMyReferencesUiState {
  selectedReferralId?: string;
  navigationState?: NavigationState;
}

let cachedMyReferencesUiState: CachedMyReferencesUiState = {};

const getReferralId = (referral?: GetMissionReferralListDto) => {
  if (!referral) {
    return undefined;
  }

  if (referral.missionReferralId || referral.missionAssignationId) {
    return referral.missionReferralId ?? referral.missionAssignationId;
  }

  if (referral.periodMissionConfigurationId || referral.branchId) {
    return `${referral.periodMissionConfigurationId ?? ''}-${referral.branchId ?? ''}`;
  }

  return undefined;
};

const getReferralUrl = (referral?: GetMissionReferralListDto, details?: GetMissionReferralDetailsResponse) => {
  if (details?.referenceLink?.trim()) {
    return details.referenceLink.trim();
  }

  const referralWithFutureUrl = referral as (GetMissionReferralListDto & { referralUrl?: string }) | undefined;
  return referralWithFutureUrl?.referralUrl?.trim();
};

const ReferralListLogo = ({ logoUrl, defaultLogo, imageStyle }: { logoUrl?: string; defaultLogo: React.ReactNode; imageStyle: any }) => {
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    setHasLoadError(false);
  }, [logoUrl]);

  const normalizedLogoUrl = logoUrl?.trim();
  const isSvgLogo = !!normalizedLogoUrl && (normalizedLogoUrl.toLowerCase().includes('.svg') || normalizedLogoUrl.toLowerCase().startsWith('data:image/svg'));

  if (!normalizedLogoUrl || hasLoadError) {
    return <>{defaultLogo}</>;
  }

  if (isSvgLogo) {
    if (Platform.OS !== 'web') {
      return <>{defaultLogo}</>;
    }

    return <SvgUri uri={normalizedLogoUrl} width="100%" height="100%" onError={() => setHasLoadError(true)} />;
  }

  return <Image style={imageStyle} source={{ uri: normalizedLogoUrl }} resizeMode="contain" onError={() => setHasLoadError(true)} />;
};

const MyReferences = () => {
  const loggedState = useAppSelector((selector) => selector.auth.logged);
  const authToken = useAppSelector((selector) => selector.auth.idToken);
  const uiLanguage = useAppSelector((selector) => selector.userPrefs.uiLanguage);
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const layout = useWindowDimensions();
  const styles = useStyles((section) => section.myMissions);
  const stylesDetail = useStyles((section) => section.missionDetailCard);
  const theme = useCustomTheme();
  const insets = useSafeAreaInsets();
  const queryClient = useQueryClient();
  const apiMissions = useApi(MissionsClient);

  const useSinglePaneLayout = Platform.OS !== 'web' || layout.width <= 768;
  const mobileDetailsScrollPaddingBottom = useSinglePaneLayout ? Math.max(insets.bottom, 0) + (Platform.OS === 'ios' ? 88 : 96) : 16;
  const defaultLogo = useMemo(() => <FontAwesomeIcon icon={faStore} style={styles.newVersion.list.logo.icon} />, [styles]);

  const [navigationEnd, setNavigationEnd] = useState(false);
  const [referralGroups, setReferralGroups] = useState<GetMissionReferralListGroupDto[] | undefined>(undefined);
  const [selectedReferralId, setSelectedReferralId] = useState<string | undefined>(cachedMyReferencesUiState.selectedReferralId ?? '');
  const [navigationState, setNavigationState] = useState<NavigationState>(cachedMyReferencesUiState.navigationState ?? NavigationState.List);
  const [expandedAccordion, setExpandedAccordion] = useState<ReferralDetailAccordionSection | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | undefined>(undefined);
  const [referMissionPopupReferral, setReferMissionPopupReferral] = useState<GetMissionReferralListDto | undefined>(undefined);

  useFocusEffect(
    React.useCallback(() => {
      if (loggedState !== undefined && loggedState !== LoggedState.NotLogged) {
        setNavigationEnd(true);
        void queryClient.invalidateQueries({ queryKey: ['missionReferrals'] });
      } else {
        setNavigationEnd(false);
      }
      return () => {
        setNavigationEnd(false);
        void queryClient.cancelQueries({ queryKey: ['missionReferrals'] });
      };
    }, [loggedState, queryClient])
  );

  useEffect(() => {
    cachedMyReferencesUiState = {
      selectedReferralId,
      navigationState,
    };
  }, [navigationState, selectedReferralId]);

  const referralSections = useMemo(
    () =>
      (referralGroups ?? [])
        .filter((group) => (group.missionsList?.length ?? 0) > 0)
        .map((group, groupIndex) => ({
          key: `referralGroup_${groupIndex}_${group.group ?? ''}`,
          title: group.group === MissionReferralListGroup.Approved ? t('references-approved') : t('references-sent'),
          data: group.missionsList ?? [],
        })),
    [referralGroups, t]
  );

  const allReferrals = useMemo(() => (referralGroups ?? []).flatMap((group) => group.missionsList ?? []), [referralGroups]);
  const selectedReferral = useMemo(() => allReferrals.find((referral) => getReferralId(referral) === selectedReferralId), [allReferrals, selectedReferralId]);

  const canLoadReferences = navigationEnd && loggedState === LoggedState.Logged && !!authToken;
  const referralsQuery = useQuery({
    enabled: canLoadReferences,
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    useErrorBoundary: false,
    queryKey: ['missionReferrals', 'list', authToken],
    queryFn: ({ signal }) => {
      if (!referralGroups || referralGroups.length === 0) {
        setLoading(true);
      }

      const promise = readMissions(token => apiMissions.getMissionReferralList(token), signal);

      return promise.finally(() => setLoading(false));
    },
  });

  useEffect(() => {
    if (referralsQuery.isSuccess) {
      const groups = (referralsQuery.data?.referrals ?? []).filter((group) => (group.missionsList?.length ?? 0) > 0);
      setReferralGroups(groups.length > 0 ? groups : undefined);

      const nextReferrals = groups.flatMap((group) => group.missionsList ?? []);
      const selectedStillExists = nextReferrals.some((referral) => getReferralId(referral) === selectedReferralId);

      if (!selectedStillExists) {
        const firstReferralId = getReferralId(nextReferrals[0]);
        setSelectedReferralId(firstReferralId ?? '');
        if (!useSinglePaneLayout && firstReferralId) {
          setNavigationState(NavigationState.Details);
        }
      }
    }

    if (referralsQuery.isError) {
      setLoading(false);
    }
  }, [referralsQuery.isSuccess, referralsQuery.isError, referralsQuery.data, selectedReferralId, useSinglePaneLayout]);

  useEffect(() => {
    if (!useSinglePaneLayout && !selectedReferralId && allReferrals.length > 0) {
      setSelectedReferralId(getReferralId(allReferrals[0]) ?? '');
      setNavigationState(NavigationState.Details);
    }
  }, [allReferrals, selectedReferralId, useSinglePaneLayout]);

  const missionDetailQuery = useQuery({
    enabled: canLoadReferences && !!selectedReferral?.missionReferralId,
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    useErrorBoundary: false,
    queryKey: ['missionReferrals', 'detail', selectedReferral?.missionReferralId, uiLanguage],
    queryFn: ({ signal }) => readMissions(token => apiMissions.getMissionReferralDetails(selectedReferral!.missionReferralId!, token), signal),
  });

  const fetchedMissionReferralDetails = missionDetailQuery.data;
  const missionReferralDetails =
    fetchedMissionReferralDetails?.missionReferralId === selectedReferral?.missionReferralId ? fetchedMissionReferralDetails : undefined;
  const loadingDetail = !missionReferralDetails && missionDetailQuery.isFetching;
  const selectedMissionDetail = useMemo<GetMissionReferralMissionDetailsDto | undefined>(
    () => missionReferralDetails?.missionDetails,
    [missionReferralDetails?.missionDetails]
  );
  const selectedGeoLocation = selectedMissionDetail?.geoLocation ?? selectedReferral?.geoLocation;
  const selectedInvitedEmail = missionReferralDetails?.invitedEmail ?? selectedReferral?.invitedEmail;
  const selectedReferralStatus = missionReferralDetails?.referralStatus ?? selectedReferral?.referralStatus;
  const selectedMissionAssignationStatus = missionReferralDetails?.missionAssignationStatus ?? selectedReferral?.missionAssignationStatus;
  const selectedSentAt = missionReferralDetails?.sentAt ?? selectedReferral?.sentAt;
  const selectedExpiresAt = missionReferralDetails?.expiresAt ?? selectedReferral?.expiresAt;
  const selectedAcceptedAt = missionReferralDetails?.acceptedAt ?? selectedReferral?.acceptedAt;
  const selectedApprovedDate = missionReferralDetails?.approvedDate ?? selectedReferral?.approvedDate;
  const selectedCanCancelReference = missionReferralDetails?.canCancelReference ?? selectedReferral?.canCancel;

  const relatedMinorRelationshipLabel = (relationship?: RelatedMinorRelationship) => {
    switch (relationship) {
      case RelatedMinorRelationship.Child:
        return t('related-minor-relationship-child');
      case RelatedMinorRelationship.Sibling:
        return t('related-minor-relationship-sibling');
      case RelatedMinorRelationship.LegalGuardian:
        return t('related-minor-relationship-legal-guardian');
      case RelatedMinorRelationship.OtherRelatedFamily:
        return t('related-minor-relationship-other-related-family');
      default:
        return undefined;
    }
  };

  const detailTextByType = useMemo(() => {
    const map: Partial<Record<MissionConfigurationDetailType, string>> = {};
    (selectedMissionDetail?.details ?? []).forEach((detail) => {
      if (detail.type !== undefined && detail.text) {
        map[detail.type] = detail.text;
      }
    });
    return map;
  }, [selectedMissionDetail?.details]);

  const cancelReferralMutation = useMutation({
    mutationFn: (referralId: string) => apiMissions.cancelMissionReferral(referralId, apiMissions.tokenSource?.token),
    onSuccess(data) {
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setContent(<></>));
      dispatch(lightBoxActions.setNoDismiss(false));

      if (data?.success === false) {
        setActionMessage({ type: 'error', text: t('reference-cancel-error') });
        return;
      }

      setActionMessage({ type: 'success', text: t('reference-cancel-success') });
      queryClient.invalidateQueries({ queryKey: ['missionReferrals'] });
      dispatch(missionsActions.setReloadMissionListSeed(String.prototype.generateRandomString(5)));
    },
    onError() {
      dispatch(lightBoxActions.setNoDismiss(false));
      setActionMessage({ type: 'error', text: t('reference-cancel-error') });
    },
  });

  const formatDate = (date?: Date) => (date ? date.toLocaleDateString() : undefined);
  const hasCoordinates = selectedGeoLocation?.x !== undefined && selectedGeoLocation?.y !== undefined;
  const showEmptyState =
    navigationEnd &&
    loggedState === LoggedState.Logged &&
    referralsQuery.isFetched &&
    !referralsQuery.isLoading &&
    !referralsQuery.isFetching &&
    !loading &&
    allReferrals.length === 0;
  const showBootstrapLoading =
    navigationEnd &&
    loggedState === LoggedState.Logged &&
    (!authToken || loading || referralsQuery.isLoading || referralsQuery.isFetching) &&
    allReferrals.length === 0;

  const openReferMissionPopup = () => {
    if (!selectedReferral?.missionReferralId) {
      return;
    }

    setActionMessage(undefined);
    setReferMissionPopupReferral(selectedReferral);
  };

  const closeReferMissionPopup = () => {
    setReferMissionPopupReferral(undefined);
  };

  const handleReferMissionEmailSent = (isResendFlow: boolean) => {
    setActionMessage({
      type: 'success',
      text: isResendFlow ? t('reference-resend-success') : t('reference-invitation-email-success'),
    });
    queryClient.invalidateQueries({ queryKey: ['missionReferrals'] });
  };

  const getTimelineStepState = (step: 'referenceSent' | 'missionAccepted' | 'missionAccomplished' | 'prevalidation' | 'validation' | 'approved') => {
    if (!selectedReferral) {
      return 'disabled';
    }

    if (selectedReferralStatus === MissionReferralStatus.InvitationCanceled) {
      return step === 'referenceSent' ? 'error' : 'disabled';
    }

    const missionStatus = selectedMissionAssignationStatus ?? MissionAssignationStatus.Available;
    const hasAccepted =
      !!selectedAcceptedAt ||
      selectedReferralStatus === MissionReferralStatus.InvitationAccepted ||
      [
        MissionAssignationStatus.Assigned,
        MissionAssignationStatus.Prevalidation,
        MissionAssignationStatus.PrevalidationFailed,
        MissionAssignationStatus.Validating,
        MissionAssignationStatus.ValidationRejected,
        MissionAssignationStatus.Approved,
      ].includes(missionStatus);
    const hasAccomplished = [
      MissionAssignationStatus.Prevalidation,
      MissionAssignationStatus.PrevalidationFailed,
      MissionAssignationStatus.Validating,
      MissionAssignationStatus.ValidationRejected,
      MissionAssignationStatus.Approved,
    ].includes(missionStatus);
    const hasPrevalidation = [
      MissionAssignationStatus.Prevalidation,
      MissionAssignationStatus.Validating,
      MissionAssignationStatus.ValidationRejected,
      MissionAssignationStatus.Approved,
    ].includes(missionStatus);
    const hasValidation = [MissionAssignationStatus.Validating, MissionAssignationStatus.Approved].includes(missionStatus);
    const hasApproved = !!selectedApprovedDate || missionStatus === MissionAssignationStatus.Approved;

    switch (step) {
      case 'referenceSent':
        return selectedSentAt ? 'active' : 'disabled';
      case 'missionAccepted':
        return hasAccepted || hasAccomplished || hasApproved ? 'active' : 'disabled';
      case 'missionAccomplished':
        return hasAccomplished || hasApproved ? 'active' : 'disabled';
      case 'prevalidation':
        return missionStatus === MissionAssignationStatus.PrevalidationFailed ? 'error' : hasPrevalidation || hasApproved ? 'active' : 'disabled';
      case 'validation':
        return missionStatus === MissionAssignationStatus.ValidationRejected ? 'error' : hasValidation || hasApproved ? 'active' : 'disabled';
      case 'approved':
        return hasApproved ? 'active' : 'disabled';
      default:
        return 'disabled';
    }
  };

  const currentTimelineStep = (['approved', 'validation', 'prevalidation', 'missionAccomplished', 'missionAccepted', 'referenceSent'] as const)
    .find(step => getTimelineStepState(step) !== 'disabled');

  const renderTimelineItem = (
    step: 'referenceSent' | 'missionAccepted' | 'missionAccomplished' | 'prevalidation' | 'validation' | 'approved',
    title: string,
    icon: IconProp,
    date?: Date
  ) => {
    const state = getTimelineStepState(step);
    const timelineStyles = styles.newVersion.details.content.timeline;
    const referenceTimelineStyles = styles.newVersion.references.timeline;
    const iconBaseStyle = state === 'active' ? { backgroundColor: REFERRAL_PURPLE } : state === 'error' ? timelineStyles.icon.error : timelineStyles.icon.disabled;
    const itemStyle =
      state === 'active' && step === currentTimelineStep ? [timelineStyles.contentActive, referenceTimelineStyles.contentActive]
        : state === 'error' ? [timelineStyles.contentError, referenceTimelineStyles.contentError] : {};
    const contentStyle = referenceTimelineStyles.text;
    const iconColor = state === 'disabled' ? REFERRAL_PURPLE : '#FFFFFF';

    return (
      <View style={[timelineStyles.item, itemStyle]}>
        {step !== 'approved' && <View style={[timelineStyles.dottedLine, referenceTimelineStyles.dottedLine, timelineStyles.dottedBottom]} />}
        {step !== 'referenceSent' && <View style={[timelineStyles.dottedLine, referenceTimelineStyles.dottedLine, timelineStyles.dottedUp]} />}
        <View style={[timelineStyles.iconBase, iconBaseStyle]}>
          <FontAwesomeIcon icon={state === 'error' ? faCircleXmark : icon} size={Platform.OS === 'web' ? 20 : 18} color={iconColor} />
        </View>
        <View style={timelineStyles.content}>
          <Label variant={LabelType.titleSmall} style={contentStyle}>
            {t(title)}
          </Label>
          {!!date && (
            <Label variant={LabelType.body1} style={contentStyle}>
              {formatDate(date)}
            </Label>
          )}
        </View>
      </View>
    );
  };

  const renderTimeline = () => (
    <View style={[styles.newVersion.details.content.timeline.container, styles.newVersion.references.timeline.container]}>
      {renderTimelineItem(
        'referenceSent',
        selectedReferralStatus === MissionReferralStatus.InvitationCanceled ? 'reference-canceled' : 'reference-sent',
        faLocation,
        selectedSentAt
      )}
      {renderTimelineItem('missionAccepted', 'Mission-accepted', faLocation, selectedAcceptedAt)}
      {renderTimelineItem('missionAccomplished', 'Mission-accomplished', faLocationCheck)}
      {renderTimelineItem('prevalidation', 'Mission-in-prevalidation', faEye)}
      {renderTimelineItem('validation', 'Mission-in-validation', faCheckDouble)}
      {renderTimelineItem('approved', 'Mission-approved', faAward, selectedApprovedDate)}
    </View>
  );

  const renderSummary = () => {
    const salaryAmount = selectedMissionDetail?.salaryAmount ?? 0;
    const salaryPoints = selectedMissionDetail?.salaryPoints ?? 0;
    const reimbursementValue = selectedMissionDetail?.reimbursementValue;

    return (
      <View style={styles.newVersion.details.content.summary.container}>
        <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
          <FontAwesomeIcon
            icon={faAward}
            color={theme.text?.primary?.toString()}
            style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
            size={20}
          />
          <Label variant={LabelType.valueLargeMission} style={{ marginLeft: 6, minWidth: 50, flexShrink: 0 }}>
            {uiLanguage === 'en' ? `$${salaryAmount}` : `${salaryAmount} $`}
          </Label>
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              {t('salary')}
            </Label>
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              ({salaryPoints} pts)
            </Label>
          </View>
        </View>
        <MissionPromotionSummary display={selectedMissionDetail?.promotionDisplay} />
        {selectedMissionDetail?.requiredPurchase && (
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
            <FontAwesomeIcon
              icon={faBasketShopping}
              color={theme.text?.primary?.toString()}
              style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
              size={20}
            />
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              {t('purchase_required')}
            </Label>
          </View>
        )}
        {!!reimbursementValue && (
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
            <FontAwesomeIcon
              icon={faReceipt}
              color={theme.text?.primary?.toString()}
              style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
              size={20}
            />
            <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValue}>
              <Label variant={LabelType.valueLargeMission} style={{ marginLeft: 6, minWidth: 50, flexShrink: 0 }}>
                {reimbursementValue}
              </Label>
              <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
                <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flexShrink: 1, fontWeight: '700' }}>
                  {t('reimbursement')}
                </Label>
              </View>
            </View>
          </View>
        )}
        {selectedMissionDetail?.selectedRelatedMinor &&
          (() => {
            const minor = selectedMissionDetail.selectedRelatedMinor;
            const name = `${minor.firstName ?? ''} ${minor.lastName ?? ''}`.trim();
            const relationship = relatedMinorRelationshipLabel(minor.relationship);

            return (
              <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
                <FontAwesomeIcon
                  icon={faChild}
                  color={theme.text?.primary?.toString()}
                  style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                  size={20}
                />
                <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
                  <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
                    {t('selected-related-minor')}
                  </Label>
                  <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flexShrink: 1, fontWeight: '700' }} numberOfLines={2}>
                    {name}
                  </Label>
                  {!!relationship && (
                    <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flexShrink: 1, fontWeight: '700' }} numberOfLines={2}>
                      {`(${relationship})`}
                    </Label>
                  )}
                </View>
              </View>
            );
          })()}
        <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
          <FontAwesomeIcon
            icon={faCheckDouble}
            color={theme.text?.primary?.toString()}
            style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
            size={20}
          />
          <Label variant={LabelType.valueLargeMission} style={{ marginLeft: 6, minWidth: 50, flexShrink: 0 }}>
            {uiLanguage === 'en' ? '$5' : '5 $'}
          </Label>
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              {t('reference-reward')}
            </Label>
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              (5 000 pts)
            </Label>
          </View>
        </View>
      </View>
    );
  };

  const renderDetailBlock = (title: string, icon: IconProp, content: string) => {
    if (!content) {
      return null;
    }

    return (
      <View style={styles.newVersion.details.content.details.infoBlocks.content}>
        <View style={styles.newVersion.details.content.details.infoBlocks.title}>
          <FontAwesomeIcon icon={icon} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
          <Label variant={LabelType.titleDark}>{title}</Label>
        </View>
        <CustomMarkdown>{content}</CustomMarkdown>
      </View>
    );
  };

  const renderAccordion = (section: ReferralDetailAccordionSection, title: string, icon: IconProp, content: string) => {
    if (!content) {
      return null;
    }

    const isExpanded = expandedAccordion === section;

    return (
      <View style={styles.newVersion.details.content.details.infoBlocks.accordion.base}>
        <Pressable
          style={styles.newVersion.details.content.details.infoBlocks.accordion.header}
          onPress={() => setExpandedAccordion((prev) => (prev === section ? undefined : section))}
        >
          <FontAwesomeIcon icon={icon} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
          <Label variant={LabelType.titleDark} style={styles.newVersion.details.content.details.infoBlocks.accordion.title}>
            {title}
          </Label>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            style={styles.newVersion.details.content.details.infoBlocks.accordion.chevron}
            size={14}
          />
        </Pressable>
        {isExpanded && (
          <View style={styles.newVersion.details.content.details.infoBlocks.accordion.content}>
            <CustomMarkdown>{content}</CustomMarkdown>
          </View>
        )}
      </View>
    );
  };

  const renderMissionContent = () => {
    const objectiveText = detailTextByType[MissionConfigurationDetailType.Objective] ?? '';
    const noteText = detailTextByType[MissionConfigurationDetailType.ToNote] ?? '';
    const niceToKnowText = detailTextByType[MissionConfigurationDetailType.NiceToKnow] ?? '';
    const scenarioText = detailTextByType[MissionConfigurationDetailType.Scenario] ?? '';
    const eligibilityText = detailTextByType[MissionConfigurationDetailType.Eligibility] ?? '';
    const termsText = detailTextByType[MissionConfigurationDetailType.TermsAndConditions] ?? '';
    const hasContent = objectiveText || noteText || niceToKnowText || scenarioText || eligibilityText || termsText
      || selectedMissionDetail?.promotionDisplay?.promotions?.some(promotion => promotion.details || promotion.startDate || promotion.endDate);

    if (loadingDetail) {
      return (
        <View style={{ minHeight: 200 }}>
          <Loading style={{ height: 200 }} />
        </View>
      );
    }

    if (!hasContent) {
      return (
        <View style={{ minHeight: 160, justifyContent: 'center', alignItems: 'center', padding: 16 }}>
          <Label style={{ textAlign: 'center', color: theme.text?.titleLight }} numberOfLines={4}>
            {t('reference-no-details')}
          </Label>
        </View>
      );
    }

    return (
      <View style={styles.newVersion.details.content.details.infoBlocksBase}>
        {renderDetailBlock(t('Your-objective'), faBullseye, objectiveText)}
        <MissionPromotionSection display={selectedMissionDetail?.promotionDisplay} appearance={styles.newVersion.details.content.details} />
        {renderDetailBlock(t('Please-note'), faPenNib, noteText)}
        {renderDetailBlock(t('Nice-to-know'), faEye, niceToKnowText)}
        {renderDetailBlock(t('The-scenario'), faStore, scenarioText)}
        {renderAccordion('admissibility', t('Admissibility'), faCheckDouble, eligibilityText)}
        {renderAccordion('conditions', t('Terms-and-Conditions'), faReceipt, termsText)}
      </View>
    );
  };

  const showCancelConfirmation = () => {
    if (!selectedReferral?.missionReferralId) {
      return;
    }

    const missionReferralId = selectedReferral.missionReferralId;
    dispatch(lightBoxActions.setNoDismiss(false));
    dispatch(
      lightBoxActions.setContent(
        <ProfileModal
          onClose={() => {
            dispatch(lightBoxActions.setVisibility(false));
            dispatch(lightBoxActions.setContent(<></>));
          }}
          onConfirm={() => {
            dispatch(lightBoxActions.setNoDismiss(true));
            cancelReferralMutation.mutate(missionReferralId);
          }}
        >
          <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              size={48}
              color={theme.isDark ? (theme.text.error?.toString() ?? '#ff9bb4') : theme.colorErrorPrimary.toString()}
            />
            <Label style={{ marginTop: 8 }}>{t('reference-cancel-confirm-title')}</Label>
            <Label
              variant={LabelType.titlePrimarySmall}
              style={{ marginTop: 8, textAlign: 'center', color: theme.isDark ? (theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA') : undefined }}
            >
              {t('reference-cancel-confirm-body')}
            </Label>
          </View>
        </ProfileModal>
      )
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const renderReferMissionPopup = () => {
    if (!referMissionPopupReferral?.missionReferralId) {
      return null;
    }

    const referMissionPopupDetails =
      missionReferralDetails?.missionReferralId === referMissionPopupReferral.missionReferralId ? missionReferralDetails : undefined;
    const invitedEmail = (referMissionPopupDetails?.invitedEmail ?? referMissionPopupReferral.invitedEmail)?.trim();
    const isResendFlow = !!invitedEmail;

    return (
      <Modal animationType="none" visible={!!referMissionPopupReferral} transparent={true} onRequestClose={closeReferMissionPopup}>
        <Overlay showOverLay={!!referMissionPopupReferral} zIndex={3000} onPress={closeReferMissionPopup}>
          <MissionReferPopup
            periodMissionConfigurationId={referMissionPopupReferral.periodMissionConfigurationId}
            branchId={referMissionPopupReferral.branchId}
            missionReferralId={referMissionPopupReferral.missionReferralId}
            referralLink={getReferralUrl(referMissionPopupReferral, referMissionPopupDetails)}
            invitedEmail={invitedEmail}
            onClose={closeReferMissionPopup}
            onEmailSent={() => handleReferMissionEmailSent(isResendFlow)}
          />
        </Overlay>
      </Modal>
    );
  };

  const renderActions = () => {
    const hasInvitedEmail = !!selectedInvitedEmail?.trim();
    const canRemindFriend = missionReferralDetails?.canRemindFriend ?? selectedReferral?.canRemindFriend;
    const awaitingQuestionnaire = selectedMissionAssignationStatus === MissionAssignationStatus.Assigned
      || selectedMissionAssignationStatus === MissionAssignationStatus.ValidationRejected
      || selectedMissionAssignationStatus === MissionAssignationStatus.PrevalidationFailed;
    const pendingInvitation = selectedReferralStatus === MissionReferralStatus.InvitationSent
      && selectedMissionAssignationStatus === MissionAssignationStatus.Referred
      && !!selectedExpiresAt && selectedExpiresAt.getTime() > Date.now();
    const canShowEmailAction = !!selectedReferral?.missionReferralId && !selectedApprovedDate
      && (pendingInvitation || (canRemindFriend === true && awaitingQuestionnaire));
    const showResend = canShowEmailAction && hasInvitedEmail;
    const showSendInvitation = canShowEmailAction && !hasInvitedEmail;
    const canCancel = !!selectedCanCancelReference && !!selectedReferral?.missionReferralId;
    const isCancelling = cancelReferralMutation.isLoading;
    const actionsDisabled = isCancelling;

    if (!showResend && !showSendInvitation && !canCancel && !actionMessage) {
      return null;
    }

    return (
      <View style={[
        styles.newVersion.details.content.actionButtonsContainer,
        useSinglePaneLayout && styles.newVersion.references.actions.container,
      ]}>
        {!!actionMessage && (
          <Label
            numberOfLines={4}
            style={{
              color: actionMessage.type === 'success' ? theme.text?.darkTurquoiseTwo : theme.text?.error,
              fontWeight: '600',
              marginBottom: 12,
              flexShrink: 1,
            }}
          >
            {actionMessage.text}
          </Label>
        )}
        {showResend && (
          <Pressable
            disabled={actionsDisabled}
            style={[
              styles.newVersion.details.content.delayMission.container,
              useSinglePaneLayout && styles.newVersion.references.actions.button,
              actionsDisabled && { opacity: 0.6 },
            ]}
            onPress={() => {
              setActionMessage(undefined);
              openReferMissionPopup();
            }}
          >
            <FontAwesomeIcon icon={faLocationCheck} size={Platform.OS === 'web' ? 32 : 24} color={'rgb(52, 114, 184)'} />
            <Label
              variant={LabelType.title}
              style={[styles.newVersion.details.content.delayMission.text, useSinglePaneLayout && styles.newVersion.references.actions.text]}
              numberOfLines={useSinglePaneLayout ? 0 : 3}
            >
              {t('reference-resend')}
            </Label>
          </Pressable>
        )}
        {showSendInvitation && (
          <Pressable
            disabled={actionsDisabled}
            style={[
              styles.newVersion.details.content.delayMission.container,
              useSinglePaneLayout && styles.newVersion.references.actions.button,
              actionsDisabled && { opacity: 0.6 },
            ]}
            onPress={openReferMissionPopup}
          >
            <FontAwesomeIcon icon={faLocationCheck} size={Platform.OS === 'web' ? 32 : 24} color={'rgb(52, 114, 184)'} />
            <Label
              variant={LabelType.title}
              style={[styles.newVersion.details.content.delayMission.text, useSinglePaneLayout && styles.newVersion.references.actions.text]}
              numberOfLines={useSinglePaneLayout ? 0 : 3}
            >
              {t('reference-send-invitation-email')}
            </Label>
          </Pressable>
        )}
        {canCancel && (
          <Pressable
            disabled={actionsDisabled}
            style={[
              styles.newVersion.details.content.cancelMission.container,
              useSinglePaneLayout && styles.newVersion.references.actions.button,
              actionsDisabled && { opacity: 0.6 },
            ]}
            onPress={() => {
              setActionMessage(undefined);
              showCancelConfirmation();
            }}
          >
            <FontAwesomeIcon icon={faCircleXmark} size={Platform.OS === 'web' ? 32 : 24} color={'rgb(138, 47, 72)'} />
            <Label
              variant={LabelType.title}
              style={[styles.newVersion.details.content.cancelMission.text, useSinglePaneLayout && styles.newVersion.references.actions.text]}
              numberOfLines={useSinglePaneLayout ? 0 : 3}
            >
              {isCancelling ? t('reference-cancelling') : t('reference-cancel')}
            </Label>
          </Pressable>
        )}
      </View>
    );
  };

  const renderEmptyState = () => (
    <View
      style={{
        height: '100%',
        flexGrow: 1,
        flexShrink: 1,
        flexDirection: 'column',
        alignContent: 'center',
        justifyContent: 'center',
        ...StylesHelper.padding(16, 24),
      }}
    >
      <Label variant={LabelType.titleLargeNoCapsPrimary} style={styles.newVersion.list.title}>
        {t('my_references')}
      </Label>
      <ScrollView style={{ flexGrow: 1, flexShrink: 1, marginTop: 16 }}>
        <View style={{ alignItems: 'center', flexShrink: 0 }}>
          <Image style={{ width: 229, height: 274, alignSelf: 'center' }} source={require('../../assets/NoValidations.png')} />
          <Label variant={LabelType.titleLargeLight} style={{ textAlign: 'center', fontWeight: '700', fontSize: 20, flexShrink: 1, marginTop: 16 }}>
            {t('references-empty-title')}
          </Label>
          <Label style={{ textAlign: 'center', alignSelf: 'center', fontSize: 14, width: 220, paddingVertical: 24, flexShrink: 1 }} numberOfLines={5}>
            {t('references-empty-body')}
          </Label>
          <View style={{ flexDirection: 'row', marginTop: 24 }}>
            <CustomButton title={t('missions-available')} onPress={() => navigation.navigate('map' as never)} />
          </View>
        </View>
      </ScrollView>
    </View>
  );

  const renderDetails = () => {
    if (!selectedReferral) {
      return null;
    }

    const missionConfigurationName = selectedMissionDetail?.missionConfigurationName ?? selectedReferral.missionConfigurationName;
    const branchName = selectedMissionDetail?.name ?? selectedReferral.name;
    const address = selectedMissionDetail?.address ?? selectedReferral.address;
    const phone = selectedMissionDetail?.phone ?? selectedReferral.phone;

    return (
      <View style={styles.newVersion.detailsContainer}>
        <ScrollView style={styles.newVersion.detailsWrapper} contentContainerStyle={{ paddingBottom: mobileDetailsScrollPaddingBottom }}>
          <View style={styles.newVersion.details.header.container}>
            <View>
              <Image style={styles.newVersion.details.header.pin} source={pin} />
            </View>
            <View style={styles.newVersion.details.header.title}>
              <Label numberOfLines={2} style={{ color: theme.text?.primary, fontSize: 16, textTransform: 'uppercase', fontWeight: '600', flexShrink: 1 }}>
                {missionConfigurationName}
              </Label>
              <Label numberOfLines={2} style={{ color: theme.text?.primary, fontSize: 13, fontWeight: '600', marginBottom: 4, flexShrink: 1 }}>
                {branchName}
              </Label>
              {hasCoordinates ? (
                <Pressable
                  onPress={() => {
                    navigation.navigate({
                      name: 'map',
                      params: { pos: selectedReferral.branchId, location: `${selectedGeoLocation!.y}|${selectedGeoLocation!.x}` },
                    } as never);
                  }}
                  style={{ flexDirection: 'row', alignItems: 'center' }}
                >
                  <Label variant={LabelType.titleDarkSmall} style={{ flexShrink: 1 }}>
                    {address}
                  </Label>
                  <FontAwesomeIcon icon={faMapLocationDot} size={14} style={{ marginLeft: 4 }} color={theme.text?.titleDark?.toString()} />
                </Pressable>
              ) : (
                <Label variant={LabelType.titleDarkSmall} style={{ flexShrink: 1 }}>
                  {address}
                </Label>
              )}
              {!!phone && (
                <Label variant={LabelType.titleDarkSmall} style={{ flexShrink: 1 }}>
                  {phone}
                </Label>
              )}
              {!!selectedInvitedEmail && (
                <Label variant={LabelType.caption} style={{ color: theme.text?.titleLight, marginTop: 4 }}>
                  {selectedInvitedEmail}
                </Label>
              )}
              {!!selectedExpiresAt && (
                <Label variant={LabelType.caption} style={{ color: theme.text?.titleLight, marginTop: 2 }}>
                  {t('reference-expires-at', { date: selectedExpiresAt.toLocaleDateString() })}
                </Label>
              )}
            </View>
          </View>
          <View style={styles.newVersion.details.content.container}>
            <View style={styles.newVersion.details.content.leftContainer}>
              {renderTimeline()}
              {renderSummary()}
              {renderActions()}
            </View>
            <View style={styles.newVersion.details.content.rightContainer}>
              <View style={styles.newVersion.details.content.details.container}>{renderMissionContent()}</View>
            </View>
          </View>
        </ScrollView>
      </View>
    );
  };

  return (
    <AuthGuard>
      {renderReferMissionPopup()}
      {loggedState === LoggedState.Anonymous && navigationEnd && (
        <LoggedInRestrictedArea message={t('need-login-before', { action: t('see-your-references') })} />
      )}
      {loggedState === LoggedState.Logged && navigationEnd && (
        <>
          {useSinglePaneLayout && navigationState === NavigationState.Details && (
            <NavigationButton
              style={styles.newVersion['detailsWrapper.backButton']}
              icon={faCircleChevronLeft}
              iconSize={24}
              labelVariant={LabelType.titleSmall}
              onPress={() => {
                setNavigationState(NavigationState.List);
                setSelectedReferralId(undefined);
              }}
              text={t('back')}
            />
          )}
          <View style={[styles.containerBase, { paddingBottom: useSinglePaneLayout ? 32 : 0 }]}>
            {(!useSinglePaneLayout || navigationState === NavigationState.List) && allReferrals.length > 0 && (
              <View style={styles.container.sideBar}>
                {!loading ? (
                  <SectionList
                    sections={referralSections}
                    keyExtractor={(referral, index) => getReferralId(referral) ?? `referral-${index}`}
                    stickySectionHeadersEnabled={false}
                    keyboardShouldPersistTaps="handled"
                    nestedScrollEnabled={true}
                    contentContainerStyle={{ paddingBottom: 16 }}
                    initialNumToRender={12}
                    maxToRenderPerBatch={12}
                    windowSize={8}
                    updateCellsBatchingPeriod={16}
                    removeClippedSubviews={Platform.OS !== 'web'}
                    ListHeaderComponent={
                      <View style={{ flexDirection: 'row' }}>
                        <Label variant={LabelType.titleLargeNoCapsPrimary} style={styles.newVersion.list.status}>
                          {t('my_references')}
                        </Label>
                      </View>
                    }
                    renderSectionHeader={({ section }) => (
                      <Label variant={LabelType.titlePrimarySmall} style={styles.newVersion.list.status}>
                        {section.title}
                      </Label>
                    )}
                    renderItem={({ item: referral }) => (
                      <Pressable
                        style={[
                          styles.newVersion.list.item,
                          getReferralId(referral) === selectedReferralId
                            ? [styles.newVersion.list.itemSelected, styles.newVersion.references.list.itemSelected]
                            : {},
                        ]}
                        onPress={() => {
                          setActionMessage(undefined);
                          setSelectedReferralId(getReferralId(referral));

                          if (Platform.OS === 'web') {
                            window.history.pushState({}, '', '/myreferences');
                          }

                          if (useSinglePaneLayout) {
                            setNavigationState(NavigationState.Details);
                          }
                        }}
                      >
                        <View style={styles.newVersion.list.logoBase}>
                          <ReferralListLogo logoUrl={referral.logoUrl} defaultLogo={defaultLogo} imageStyle={styles.newVersion.list.logo.image} />
                        </View>
                        <View style={{ flexShrink: 1 }}>
                          <Label numberOfLines={2} style={[{ color: theme.text?.primary, fontSize: 16, textTransform: 'uppercase', fontWeight: '600' },
                            getReferralId(referral) === selectedReferralId ? styles.newVersion.references.list.itemSelectedText : {}]}>
                            {referral.missionConfigurationName}
                          </Label>
                          <Label numberOfLines={2} style={[{ color: theme.text?.primary, fontSize: 13, fontWeight: '600', marginBottom: 4 },
                            getReferralId(referral) === selectedReferralId ? styles.newVersion.references.list.itemSelectedText : {}]}>
                            {referral.name}
                          </Label>
                          <Label style={[{ color: theme.text?.primary, fontSize: 12, fontWeight: '400' },
                            getReferralId(referral) === selectedReferralId ? styles.newVersion.references.list.itemSelectedText : {}]}>{referral.address}</Label>
                        </View>
                      </Pressable>
                    )}
                  />
                ) : (
                  <View style={{ flexGrow: 1 }}>
                    <Loading style={{ height: 200 }} text={t('references-loading')} />
                  </View>
                )}
              </View>
            )}
            {(!useSinglePaneLayout || navigationState === NavigationState.Details) && allReferrals.length > 0 && renderDetails()}
            {showBootstrapLoading && (
              <View style={{ flexGrow: 1 }}>
                <Loading style={{ height: 200 }} text={t('references-loading')} />
              </View>
            )}
            {showEmptyState && renderEmptyState()}
          </View>
        </>
      )}
    </AuthGuard>
  );
};

export default MyReferences;
